import {
  ArrowRight,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Building2,
  Truck,
  ClipboardCheck,
  Brain,
  Database,
  Cog,
  Eye,
  Bell,
  Activity,
  Workflow,
  Layers,
  CheckCircle2,
  Search,
  Settings as SettingsIcon,
  Bot,
  MessageSquare,
} from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import SEO from "../components/SEO";

const HERO_IMAGE =
  "/images/enterprise-apps-hero-banner.webp";

const SMARTINSPECT_IMAGE =
  "/images/image-15.webp";

const SMARTRF_IMAGE =
  "/images/image-17.webp";

const SMARTCENTER_IMAGE =
  "/images/image-19.webp";

const SMARTINSPECT_HREF = "/warehousex/smartintegrations/smartinspect";
const SMARTRF_HREF = "/warehousex/smartexecution/smartrf";

const BRAND_BLUE = "#025979";
const BRAND_BLUE_DARK = "#03738C";

// --- Atoms (consistent with PlatformsPage / InnovationPage) ----------------
const Eyebrow = ({ children, tone = "blue" }) => (
  <p
    className={`text-[11px] font-semibold tracking-[0.22em] uppercase mb-3 ${
      tone === "purple" ? "text-[#7C3AED]" : "text-[#025979]"
    }`}
  >
    {children}
  </p>
);

const H2 = ({ children, className = "" }) => (
  <h2
    className={`text-3xl sm:text-4xl lg:text-[42px] font-bold tracking-tight text-[#0F172A] leading-[1.1] ${className}`}
    style={{ fontFamily: "Chivo, sans-serif" }}
  >
    {children}
  </h2>
);

const Lead = ({ children, className = "" }) => (
  <p
    className={`mt-5 text-base sm:text-lg text-[#475569] leading-relaxed max-w-3xl ${className}`}
  >
    {children}
  </p>
);

const PrimaryButton = ({ href, onClick, children, testId, target, color = "blue" }) => {
  const bg = color === "blue" ? "#2563EB" : BRAND_BLUE;
  const bgHover = color === "blue" ? "#1D4ED8" : BRAND_BLUE_DARK;
  const extra = target ? { target, rel: "noopener noreferrer" } : {};
  return (
    <a
      href={href}
      onClick={onClick}
      data-testid={testId}
      {...extra}
      className="group inline-flex items-center justify-center gap-2 rounded-sm h-11 px-6 text-xs font-semibold tracking-wide text-white transition-colors"
      style={{ backgroundColor: bg }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = bgHover)}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = bg)}
    >
      {children}
      <ArrowRight
        className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5"
        strokeWidth={2}
      />
    </a>
  );
};

const OutlineButtonOnDark = ({ href, onClick, children, testId }) => (
  <a
    href={href}
    onClick={onClick}
    data-testid={testId}
    className="group inline-flex items-center justify-center gap-2 rounded-sm h-11 px-6 text-xs font-semibold tracking-wide border border-white/30 bg-white/5 text-white hover:bg-white/10 hover:border-white/60 transition-colors backdrop-blur-sm"
  >
    {children}
    <ArrowRight
      className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5"
      strokeWidth={2}
    />
  </a>
);

const DisabledChip = ({ children, testId }) => (
  <span
    data-testid={testId}
    className="inline-flex items-center justify-center gap-2 rounded-sm h-11 px-6 text-xs font-semibold tracking-wide border border-[#E5E7EB] bg-[#F8FAFC] text-[#64748B] cursor-not-allowed select-none"
  >
    {children}
  </span>
);

const WipBadge = () => (
  <span
    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border text-[9.5px] font-semibold tracking-[0.18em] uppercase"
    style={{
      borderColor: "rgba(167,139,250,0.5)",
      color: "#7C3AED",
      background: "rgba(167,139,250,0.08)",
    }}
  >
    <span className="relative inline-flex w-1.5 h-1.5">
      <span
        className="absolute inline-flex w-full h-full rounded-full opacity-75 animate-ping"
        style={{ backgroundColor: "#A78BFA" }}
      />
      <span
        className="relative inline-flex w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: "#A78BFA" }}
      />
    </span>
    Work in Progress
  </span>
);

// --- 1. HERO ---------------------------------------------------------------
const Hero = () => (
  <section
    data-testid="enterprise-apps-hero"
    className="relative overflow-hidden text-white"
    style={{
      background:
        "linear-gradient(135deg, #050B22 0%, #0A1330 35%, #0B1734 70%, #050B22 100%)",
    }}
  >
    {/* Full-bleed cinematic hero artwork */}
    <img
      src={HERO_IMAGE}
      alt="PrimeS4 AI Enterprise Apps — SmartCenter operational dashboard, SmartRF handheld and SmartInspect tablet in a connected warehouse"
      className="absolute inset-0 w-full h-full object-cover object-[68%_center] md:object-center select-none pointer-events-none"
      loading="eager"
      draggable="false"
      data-testid="enterprise-apps-hero-artwork"
      aria-hidden="true"
    />
    {/* Stronger left-side dark wash for readability (desktop) */}
    <div
      className="absolute inset-0 pointer-events-none hidden md:block"
      style={{
        background:
          "linear-gradient(90deg, rgba(5,11,34,0.94) 0%, rgba(5,11,34,0.82) 25%, rgba(11,23,52,0.55) 50%, rgba(11,23,52,0.25) 75%, rgba(11,23,52,0.15) 100%)",
      }}
    />
    {/* Significantly stronger mobile-only overlay — Enterprise Apps hero is
        the busiest of the three, so we crush brightness behind the copy. */}
    <div
      className="absolute inset-0 pointer-events-none md:hidden"
      style={{
        background:
          "linear-gradient(180deg, rgba(5,11,34,0.95) 0%, rgba(5,11,34,0.82) 45%, rgba(5,11,34,0.7) 100%)",
      }}
    />
    {/* Global navy wash to keep cinematic mood consistent */}
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "linear-gradient(180deg, rgba(5,11,34,0.45) 0%, rgba(5,11,34,0.15) 50%, rgba(5,11,34,0.55) 100%)",
      }}
    />
    {/* Enterprise grid */}
    <div
      className="absolute inset-0 pointer-events-none opacity-[0.05]"
      style={{
        backgroundImage:
          "linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)",
        backgroundSize: "56px 56px",
        maskImage: "radial-gradient(ellipse at center, black 55%, transparent 90%)",
        WebkitMaskImage: "radial-gradient(ellipse at center, black 55%, transparent 90%)",
      }}
    />
    {/* Bottom hairline */}
    <div
      className="absolute inset-x-0 bottom-0 h-px pointer-events-none"
      style={{
        background:
          "linear-gradient(90deg, transparent 0%, rgba(94,184,216,0.45) 50%, transparent 100%)",
      }}
    />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-32 lg:pb-36 min-h-[88vh] md:min-h-0 flex flex-col justify-center">
      <div className="max-w-2xl" data-testid="enterprise-apps-hero-copy">
        <p className="text-[11px] font-semibold tracking-[0.28em] uppercase text-[#5EB8D8] mb-6 flex items-center gap-3">
          <span className="inline-block h-px w-8 bg-[#5EB8D8]" />
          Enterprise Applications
        </p>
        <h1
          className="text-[34px] sm:text-5xl lg:text-[52px] xl:text-[56px] font-bold tracking-tight leading-[1.05]"
          style={{ fontFamily: "Chivo, sans-serif" }}
          data-testid="enterprise-apps-hero-headline"
        >
          <span>Enterprise Apps Built for </span>
          <span
            className="bg-clip-text text-transparent"
            style={{ backgroundImage: "linear-gradient(90deg, #5EB8D8 0%, #A78BFA 100%)" }}
          >
            Intelligent Execution
          </span>
        </h1>
        <p
          className="mt-8 text-base sm:text-lg text-white/80 leading-relaxed max-w-xl"
          data-testid="enterprise-apps-hero-sub"
        >
          Operational applications designed to enhance visibility, execution, mobility, inspection intelligence, warehouse operations, and AI-assisted decision support across SAP-connected enterprise environments.
        </p>
        <div
          className="mt-12 flex flex-col sm:flex-row sm:flex-wrap gap-3 [&>a]:w-full [&>button]:w-full sm:[&>a]:w-auto sm:[&>button]:w-auto"
          data-testid="enterprise-apps-hero-ctas"
        >
          <PrimaryButton
            href={SMARTINSPECT_HREF}
            testId="enterprise-apps-hero-cta-smartinspect"
            color="blue"
          >
            Explore SmartInspect
          </PrimaryButton>
          <OutlineButtonOnDark href={SMARTRF_HREF} testId="enterprise-apps-hero-cta-smartrf">
            Explore SmartRF
          </OutlineButtonOnDark>
        </div>
        <div
          className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-5 max-w-2xl"
          data-testid="enterprise-apps-hero-tags"
        >
          {[
            { icon: Layers, label: "SAP-Connected" },
            { icon: Smartphone, label: "Mobile-First" },
            { icon: Sparkles, label: "AI-Assisted" },
            { icon: ShieldCheck, label: "Enterprise-Grade" },
          ].map((it) => (
            <div
              key={it.label}
              className="flex items-center gap-3 text-[12px] font-semibold tracking-[0.12em] uppercase text-white/85"
              data-testid={`enterprise-apps-hero-tag-${it.label
                .toLowerCase()
                .replace(/\s+/g, "-")}`}
            >
              <it.icon className="w-4 h-4 shrink-0 text-[#5EB8D8]" strokeWidth={1.5} />
              <span>{it.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// --- Custom visual: SmartCenter dashboard mock -----------------------------
const SmartCenterMock = () => (
  <div
    className="relative w-full aspect-[4/3] rounded-sm overflow-hidden border"
    style={{
      borderColor: "rgba(94,184,216,0.25)",
      background:
        "linear-gradient(135deg, #0A1330 0%, #0E1A3F 50%, #0B1734 100%)",
    }}
    data-testid="smartcenter-mock"
    aria-hidden="true"
  >
    {/* Subtle grid */}
    <div
      className="absolute inset-0 opacity-[0.08]"
      style={{
        backgroundImage:
          "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
        backgroundSize: "32px 32px",
      }}
    />
    {/* Top bar */}
    <div className="relative flex items-center justify-between px-5 py-3 border-b border-white/10">
      <div className="flex items-center gap-2">
        <span className="text-[12px] font-bold tracking-wide text-white">PRIVA</span>
        <span className="text-[12px] font-semibold tracking-wide text-[#5EB8D8]">
          SmartCenter
        </span>
      </div>
      <div className="flex items-center gap-3 text-white/60">
        <Search className="w-3 h-3" strokeWidth={1.8} />
        <Bell className="w-3 h-3" strokeWidth={1.8} />
        <SettingsIcon className="w-3 h-3" strokeWidth={1.8} />
      </div>
    </div>

    {/* Body */}
    <div className="relative grid grid-cols-12 gap-3 p-5 h-[calc(100%-44px)]">
      {/* Sidebar */}
      <div className="col-span-3 flex flex-col gap-2">
        {[
          { icon: Activity, label: "Dashboard", active: true },
          { icon: Truck, label: "Shipments" },
          { icon: ClipboardCheck, label: "Inspections" },
          { icon: Eye, label: "Yard" },
          { icon: Database, label: "Inventory" },
          { icon: Bell, label: "Alerts" },
        ].map((it) => (
          <div
            key={it.label}
            className={`flex items-center gap-2 px-2 py-1.5 rounded-sm text-[10px] font-medium ${
              it.active
                ? "bg-[#1E40AF]/40 text-white border border-[#5EB8D8]/40"
                : "text-white/55"
            }`}
          >
            <it.icon className="w-3 h-3" strokeWidth={1.8} />
            <span className="truncate">{it.label}</span>
          </div>
        ))}
      </div>

      {/* Main panel */}
      <div className="col-span-9 flex flex-col gap-3">
        <p className="text-[11px] font-semibold tracking-wide text-white/75">
          Operational Overview
        </p>
        <div className="grid grid-cols-4 gap-2">
          {[
            { label: "Shipments", val: "245", delta: "+18%" },
            { label: "Inspections", val: "132", delta: "+22%" },
            { label: "On-Time", val: "96%", delta: "+8%" },
            { label: "Alerts", val: "7", delta: "Active" },
          ].map((k) => (
            <div
              key={k.label}
              className="rounded-sm border border-white/10 bg-white/[0.03] px-2.5 py-2"
            >
              <p className="text-[8.5px] font-medium tracking-wide text-white/55 uppercase">
                {k.label}
              </p>
              <p className="mt-1 text-[16px] font-bold text-white tabular-nums leading-none">
                {k.val}
              </p>
              <p className="mt-1 text-[8.5px] font-semibold text-[#5EB8D8]">{k.delta}</p>
            </div>
          ))}
        </div>
        {/* Chart blocks */}
        <div className="grid grid-cols-2 gap-2 flex-1 min-h-0">
          <div className="rounded-sm border border-white/10 bg-white/[0.03] p-3 flex flex-col">
            <p className="text-[9px] font-semibold tracking-wide text-white/65">
              Inspection Trends
            </p>
            <div className="mt-2 flex-1 flex items-end gap-1">
              {[18, 30, 22, 45, 38, 52, 60, 48, 70, 65, 80].map((h, i) => (
                <div
                  key={i}
                  className="flex-1 rounded-t-[1px]"
                  style={{
                    height: `${h}%`,
                    background:
                      "linear-gradient(180deg, #5EB8D8 0%, rgba(94,184,216,0.2) 100%)",
                  }}
                />
              ))}
            </div>
          </div>
          <div className="rounded-sm border border-white/10 bg-white/[0.03] p-3 flex flex-col">
            <p className="text-[9px] font-semibold tracking-wide text-white/65">
              Yard Activity
            </p>
            <div className="mt-2 flex-1 flex items-end gap-1">
              {[35, 50, 42, 58, 64, 55, 70, 75, 60, 82, 90].map((h, i) => (
                <div
                  key={i}
                  className="flex-1 rounded-t-[1px]"
                  style={{
                    height: `${h}%`,
                    background:
                      "linear-gradient(180deg, #A78BFA 0%, rgba(167,139,250,0.2) 100%)",
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Soft inner glow */}
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        boxShadow:
          "inset 0 0 80px rgba(94,184,216,0.08), inset 0 0 40px rgba(167,139,250,0.06)",
      }}
    />
  </div>
);

// --- Custom visual: SmartEWM conversational assistant mock -----------------
const SmartEwmMock = () => (
  <div
    className="relative w-full aspect-[4/3] rounded-sm overflow-hidden border bg-white"
    style={{ borderColor: "#E5E7EB" }}
    data-testid="smartewm-mock"
    aria-hidden="true"
  >
    {/* Header */}
    <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E7EB] bg-[#FAFAFF]">
      <div className="flex items-center gap-2">
        <div
          className="w-6 h-6 rounded-sm flex items-center justify-center"
          style={{ background: "linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)" }}
        >
          <Bot className="w-3.5 h-3.5 text-[#7C3AED]" strokeWidth={1.8} />
        </div>
        <span className="text-[12px] font-semibold text-[#0F172A]">SmartEWM</span>
        <span className="text-[10px] font-medium text-[#64748B]">Operational Assistant</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="relative inline-flex w-2 h-2">
          <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
          <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-500" />
        </span>
        <span className="text-[9.5px] font-semibold tracking-wide text-emerald-600 uppercase">
          Online
        </span>
      </div>
    </div>

    {/* Chat body */}
    <div className="px-5 py-4 space-y-3">
      {/* User message */}
      <div className="flex justify-end">
        <div className="max-w-[75%] rounded-md rounded-tr-sm bg-[#EFF6FF] border border-[#DBEAFE] px-3 py-2">
          <p className="text-[11.5px] text-[#0F172A] leading-snug">
            Why is HU 8001234567 stuck in outbound staging?
          </p>
        </div>
      </div>
      {/* Assistant message */}
      <div className="flex gap-2">
        <div
          className="w-6 h-6 rounded-sm flex items-center justify-center shrink-0 mt-0.5"
          style={{ background: "linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)" }}
        >
          <Sparkles className="w-3 h-3 text-[#7C3AED]" strokeWidth={1.8} />
        </div>
        <div className="max-w-[80%] rounded-md rounded-tl-sm bg-[#F8FAFC] border border-[#E5E7EB] px-3 py-2.5">
          <p className="text-[11.5px] text-[#0F172A] leading-snug">
            HU is blocked by a pending <strong>VAS step (label print)</strong>. Wave{" "}
            <strong>WV-2421</strong> still has 3 open tasks for picker M01.
          </p>
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-white border border-[#E5E7EB] text-[9.5px] text-[#475569]">
              <ClipboardCheck className="w-2.5 h-2.5 text-[#025979]" strokeWidth={1.8} />
              Open VAS
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-white border border-[#E5E7EB] text-[9.5px] text-[#475569]">
              <Workflow className="w-2.5 h-2.5 text-[#025979]" strokeWidth={1.8} />
              Reassign Tasks
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-white border border-[#E5E7EB] text-[9.5px] text-[#475569]">
              <Activity className="w-2.5 h-2.5 text-[#025979]" strokeWidth={1.8} />
              View Wave
            </span>
          </div>
        </div>
      </div>
      {/* Suggested follow-ups */}
      <div className="flex gap-2 flex-wrap pt-1">
        {["Reroute to staging-02", "Notify supervisor", "Explain root cause"].map((s) => (
          <span
            key={s}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-[#E5E7EB] bg-white text-[10px] text-[#475569]"
          >
            <MessageSquare className="w-2.5 h-2.5 text-[#7C3AED]" strokeWidth={1.8} />
            {s}
          </span>
        ))}
      </div>
    </div>

    {/* Footer input */}
    <div className="absolute inset-x-0 bottom-0 px-5 py-3 border-t border-[#E5E7EB] bg-white">
      <div className="flex items-center gap-2 px-3 py-2 rounded-sm border border-[#E5E7EB] bg-[#FAFAFF]">
        <MessageSquare className="w-3.5 h-3.5 text-[#94A3B8]" strokeWidth={1.8} />
        <span className="text-[10.5px] text-[#94A3B8]">
          Ask about waves, tasks, putaway, exceptions…
        </span>
      </div>
    </div>
  </div>
);

// --- 2. REAL APPLICATIONS --------------------------------------------------
const AppCard = ({
  testId,
  visual,
  visualKind = "image", // "image" | "node"
  title,
  subtitle,
  description,
  capabilities,
  ctaLabel,
  ctaHref,
  badge,
  ctaDisabled,
}) => (
  <article
    data-testid={testId}
    className="group bg-white border border-[#E5E7EB] rounded-md overflow-hidden flex flex-col transition-all duration-200 hover:border-[#5EB8D8] hover:shadow-lg"
  >
    <div
      className="h-1"
      style={{
        background: "linear-gradient(90deg, #5EB8D8 0%, #7C3AED 50%, #5EB8D8 100%)",
      }}
    />
    <div className="p-2.5 sm:p-3 bg-[#F8FAFC] border-b border-[#E5E7EB]">
      {visualKind === "image" ? (
        <div className="aspect-[4/3] w-full rounded-sm overflow-hidden bg-white">
          <img
            src={visual}
            alt={`${title} product visual`}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      ) : (
        visual
      )}
    </div>
    <div className="p-6 lg:p-7 flex-1 flex flex-col">
      <div className="flex items-start justify-between gap-3">
        <h3
          className="text-[22px] font-bold tracking-tight text-[#0F172A]"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          {title}
        </h3>
        {badge}
      </div>
      <p className="mt-1.5 text-[13px] font-semibold text-[#025979]">{subtitle}</p>
      <p className="mt-4 text-[13.5px] text-[#475569] leading-relaxed">{description}</p>
      <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
        {capabilities.map((c) => (
          <li
            key={c}
            className="flex items-start gap-2 text-[12.5px] text-[#334155]"
          >
            <CheckCircle2
              className="w-3.5 h-3.5 mt-0.5 text-[#0093cb] shrink-0"
              strokeWidth={2}
            />
            <span>{c}</span>
          </li>
        ))}
      </ul>
      <div className="mt-7 pt-5 border-t border-[#F1F5F9] flex">
        {ctaDisabled ? (
          <DisabledChip testId={`${testId}-cta`}>{ctaLabel}</DisabledChip>
        ) : (
          <PrimaryButton href={ctaHref} testId={`${testId}-cta`} color="blue">
            {ctaLabel}
          </PrimaryButton>
        )}
      </div>
    </div>
  </article>
);

const RealApplications = () => (
  <section
    data-testid="section-real-applications"
    className="py-20 lg:py-28 bg-white"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl">
        <Eyebrow>Real Applications</Eyebrow>
        <H2>Applications Designed for Warehouse &amp; Logistics Operations</H2>
        <Lead>
          PrimeS4 AI enterprise applications combine operational intelligence, execution mobility, inspection workflows, and SAP-connected automation into usable operational products.
        </Lead>
      </div>

      <div className="mt-14 grid lg:grid-cols-2 gap-6 lg:gap-8" data-testid="apps-grid">
        <AppCard
          testId="app-card-smartinspect"
          visual={SMARTINSPECT_IMAGE}
          title="SmartInspect"
          subtitle="AI-Ready Logistics Inspection & Claims Intelligence Platform"
          description="Digital inspection workflows for trucks, containers, logistics assets, and warehouse operations with operational evidence capture, analytics, and SAP-connected intelligence."
          capabilities={[
            "Container Inspection",
            "Truck Inspection",
            "Claims Evidence",
            "AI Insights",
            "SAP Integration",
          ]}
          ctaLabel="Explore SmartInspect"
          ctaHref={SMARTINSPECT_HREF}
        />

        <AppCard
          testId="app-card-smartrf"
          visual={SMARTRF_IMAGE}
          title="SmartRF"
          subtitle="Modern SAP Warehouse Mobility & Execution Platform"
          description="Enterprise warehouse mobility solution designed for SAP EWM and warehouse execution workflows with responsive UX, scanning workflows, offline capability, and operational efficiency."
          capabilities={[
            "SAP EWM Mobility",
            "RF Workflows",
            "Mobile Picking",
            "Inventory Operations",
            "Offline Execution",
          ]}
          ctaLabel="Explore SmartRF"
          ctaHref={SMARTRF_HREF}
        />

        <AppCard
          testId="app-card-smartcenter"
          visual={SMARTCENTER_IMAGE}
          title="SmartCenter"
          subtitle="Operational Visibility & Execution Intelligence"
          description="Unified operational monitoring and visibility layer providing warehouse telemetry, execution dashboards, alerts, exceptions, and intelligent operational oversight."
          capabilities={[
            "Operational Dashboards",
            "Alert Monitoring",
            "Visibility Layer",
            "Execution Intelligence",
            "Exception Tracking",
          ]}
          ctaLabel="Coming Soon"
          ctaDisabled
          badge={<WipBadge />}
        />

        <AppCard
          testId="app-card-smartewm"
          visual={<SmartEwmMock />}
          visualKind="node"
          title="SmartEWM"
          subtitle="AI-Assisted SAP Warehouse Execution Intelligence"
          description="Operational assistant designed to support warehouse users, supervisors, and support teams with intelligent execution guidance, contextual recommendations, and workflow assistance."
          capabilities={[
            "Warehouse Assistance",
            "Supervisor Intelligence",
            "SAP EWM Support",
            "AI Guidance",
            "Operational Q&A",
          ]}
          ctaLabel="Coming Soon"
          ctaDisabled
          badge={<WipBadge />}
        />
      </div>
    </div>
  </section>
);

// --- 3. CONNECTED ENTERPRISE EXECUTION -------------------------------------
const ConnectedExecution = () => {
  const apps = [
    { icon: ClipboardCheck, label: "SmartInspect", tag: "Inspection" },
    { icon: Smartphone, label: "SmartRF", tag: "Mobility" },
    { icon: Activity, label: "SmartCenter", tag: "Visibility" },
    { icon: Bot, label: "SmartEWM", tag: "Intelligence" },
  ];
  return (
    <section
      data-testid="section-connected-execution"
      className="py-20 lg:py-28 bg-[#F8FAFC] border-y border-[#E5E7EB]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <Eyebrow tone="purple">Connected Execution</Eyebrow>
          <H2 className="!mx-auto">Connected Operational Applications</H2>
          <p className="mt-5 text-base sm:text-lg text-[#475569] leading-relaxed">
            Enterprise apps connected through PRIVA operational intelligence and SAP-native execution frameworks.
          </p>
        </div>

        <div className="mt-14 max-w-5xl mx-auto" data-testid="execution-flow">
          {/* PRIVA hub */}
          <div className="flex justify-center">
            <div
              className="relative inline-flex items-center gap-3 px-6 py-3 rounded-sm border"
              style={{
                background:
                  "linear-gradient(135deg, rgba(125,211,252,0.06) 0%, rgba(167,139,250,0.10) 100%)",
                borderColor: "rgba(167,139,250,0.4)",
                boxShadow: "0 0 40px rgba(167,139,250,0.18)",
              }}
              data-testid="execution-flow-priva"
            >
              <div
                className="w-9 h-9 rounded-sm flex items-center justify-center"
                style={{ background: "linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)" }}
              >
                <Layers className="w-4 h-4 text-[#7C3AED]" strokeWidth={1.6} />
              </div>
              <div className="flex flex-col leading-tight">
                <span className="text-[10px] font-semibold tracking-[0.22em] uppercase text-[#7C3AED]">
                  PRIVA
                </span>
                <span className="text-[13px] text-[#475569]">
                  Operational Intelligence Layer
                </span>
              </div>
            </div>
          </div>

          {/* Connector */}
          <div className="relative flex justify-center mt-3 mb-3">
            <div
              className="h-12 w-px"
              style={{
                background:
                  "linear-gradient(180deg, rgba(167,139,250,0.7) 0%, rgba(94,184,216,0.5) 100%)",
              }}
            />
          </div>

          {/* Apps tier */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {apps.map((a) => (
              <div
                key={a.label}
                data-testid={`execution-flow-${a.label.toLowerCase()}`}
                className="group bg-white border border-[#E5E7EB] rounded-sm p-5 transition-all hover:border-[#5EB8D8] hover:shadow-md hover:-translate-y-0.5"
              >
                <div
                  className="w-10 h-10 rounded-sm flex items-center justify-center mb-4"
                  style={{
                    background:
                      "linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)",
                  }}
                >
                  <a.icon className="w-4 h-4 text-[#025979]" strokeWidth={1.6} />
                </div>
                <h3 className="text-[14.5px] font-semibold text-[#0F172A]">{a.label}</h3>
                <p className="mt-1 text-[11px] font-semibold tracking-[0.16em] uppercase text-[#025979]">
                  {a.tag}
                </p>
              </div>
            ))}
          </div>

          {/* Supporting capability labels */}
          <div
            className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3"
            data-testid="execution-flow-labels"
          >
            {[
              { icon: Eye, label: "Visibility" },
              { icon: Smartphone, label: "Mobility" },
              { icon: ClipboardCheck, label: "Inspection" },
              { icon: Brain, label: "Intelligence" },
              { icon: Cog, label: "Automation" },
            ].map((l) => (
              <div
                key={l.label}
                className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.14em] uppercase text-[#475569]"
              >
                <l.icon className="w-3.5 h-3.5 text-[#7C3AED]" strokeWidth={1.7} />
                {l.label}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// --- 4. WHY PRIMES4 AI APPS ------------------------------------------------
const WhyPrimeS4 = () => {
  const items = [
    {
      icon: Building2,
      title: "Operationally Focused",
      desc: "Built for real warehouse and logistics workflows.",
    },
    {
      icon: Layers,
      title: "SAP-Native",
      desc: "Designed around SAP EWM and SAP-connected execution.",
    },
    {
      icon: Sparkles,
      title: "AI-Assisted",
      desc: "Embedded operational intelligence and contextual support.",
    },
    {
      icon: ShieldCheck,
      title: "Enterprise-Ready",
      desc: "Scalable, secure, and designed for enterprise operations.",
    },
  ];
  return (
    <section
      data-testid="section-why-primes4"
      className="relative py-20 lg:py-28 text-white overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, #050B22 0%, #0A1330 50%, #0B1734 100%)",
      }}
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage: "radial-gradient(ellipse at center, black 60%, transparent 95%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, black 60%, transparent 95%)",
        }}
      />
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute -top-32 left-1/4 w-[420px] h-[420px] rounded-full blur-3xl opacity-20"
          style={{ background: "radial-gradient(circle, #1E40AF 0%, transparent 65%)" }}
        />
        <div
          className="absolute bottom-0 right-1/4 w-[360px] h-[360px] rounded-full blur-3xl opacity-15"
          style={{ background: "radial-gradient(circle, #7C3AED 0%, transparent 65%)" }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <p className="text-[11px] font-semibold tracking-[0.28em] uppercase text-[#5EB8D8] mb-4 flex items-center justify-center gap-3">
            <span className="inline-block h-px w-8 bg-[#5EB8D8]" />
            Why PrimeS4 AI Apps
            <span className="inline-block h-px w-8 bg-[#5EB8D8]" />
          </p>
          <h2
            className="text-3xl sm:text-4xl lg:text-[42px] font-bold tracking-tight leading-[1.1]"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Built for Real Enterprise Operations
          </h2>
        </div>

        <div
          className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5"
          data-testid="why-primes4-grid"
        >
          {items.map((it) => (
            <div
              key={it.title}
              className="rounded-sm p-6 border bg-white/[0.03] transition-all hover:bg-white/[0.06]"
              style={{ borderColor: "rgba(255,255,255,0.10)" }}
              data-testid={`why-primes4-${it.title
                .toLowerCase()
                .replace(/\s+/g, "-")}`}
            >
              <div
                className="w-10 h-10 rounded-sm flex items-center justify-center mb-5"
                style={{
                  background: "rgba(94,184,216,0.12)",
                  border: "1px solid rgba(94,184,216,0.30)",
                }}
              >
                <it.icon className="w-4 h-4 text-[#7DD3FC]" strokeWidth={1.6} />
              </div>
              <h3 className="text-[15px] font-semibold text-white leading-snug">
                {it.title}
              </h3>
              <p className="mt-2 text-[12.5px] text-white/70 leading-relaxed">
                {it.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- 5. FINAL CTA ----------------------------------------------------------
const FinalCta = () => (
  <section
    data-testid="section-enterprise-apps-cta"
    className="py-20 lg:py-24 bg-white"
  >
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <Eyebrow>Get Started</Eyebrow>
      <H2 className="!mx-auto">Transform Warehouse Execution with PrimeS4 AI</H2>
      <p className="mt-5 text-base sm:text-lg text-[#475569] leading-relaxed max-w-3xl mx-auto">
        Modern operational applications designed for intelligent enterprise execution.
      </p>
      <div className="mt-9 flex flex-wrap justify-center gap-3">
        <button
          type="button"
          data-testid="enterprise-apps-cta-demo"
          onClick={() => window.dispatchEvent(new CustomEvent("requestDemo:open"))}
          className="group inline-flex items-center justify-center gap-2 rounded-sm h-11 px-6 text-xs font-semibold tracking-wide text-white transition-colors"
          style={{ backgroundColor: "#2563EB" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#1D4ED8")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#2563EB")}
        >
          Request Demo
          <ArrowRight
            className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5"
            strokeWidth={2}
          />
        </button>
        <a
          href="/warehousex"
          data-testid="enterprise-apps-cta-warehousex"
          className="group inline-flex items-center justify-center gap-2 rounded-sm h-11 px-6 text-xs font-semibold tracking-wide border bg-white transition-colors"
          style={{ borderColor: BRAND_BLUE, color: BRAND_BLUE }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#E0F2FE")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "white")}
        >
          Explore WarehouseX
          <ArrowRight
            className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5"
            strokeWidth={2}
          />
        </a>
      </div>
    </div>
  </section>
);

// --- Page ------------------------------------------------------------------
const EnterpriseAppsPage = () => (
  <div className="min-h-screen flex flex-col bg-white" data-testid="enterprise-apps-page">
    <SEO
      title="Enterprise Applications | PrimeS4 AI"
      description="Purpose-built enterprise applications powered by PRIVA — SmartInspect, SmartRF, SmartCenter, and SmartEWM — extending SAP with intelligent execution across warehouse operations."
      keywords={[
        "Enterprise Applications",
        "SmartInspect",
        "SmartRF",
        "SmartCenter",
        "SmartEWM",
        "SAP EWM AI",
        "Warehouse automation",
        "Intelligent warehouse operations",
      ]}
      canonical="/enterprise-apps"
      imageAlt="PrimeS4 enterprise applications — SmartInspect, SmartRF, SmartCenter, SmartEWM"
    />
    <Header />
    <main className="flex-1">
      <Hero />
      <RealApplications />
      <ConnectedExecution />
      <WhyPrimeS4 />
      <FinalCta />
    </main>
    <Footer />
  </div>
);

export default EnterpriseAppsPage;
